import { useState } from 'react';
import { Carregando, Selo, useDecisao } from '../../../common/interface';
import { moeda } from '../../formato';
import { usePacotes } from './useCreditos';
import { PixRecargaSheet } from './PixRecargaSheet';
import type { PacoteCredito } from './types';

function kmDoPacote(p: PacoteCredito): number {
  // floor, nunca round — prometer km que não cabe é o erro que só aparece
  // na emissão que falha (mesma disciplina do app, cteatf_valor.dart).
  return p.valorPorKm > 0 ? Math.floor(p.valorCredito / p.valorPorKm) : 0;
}

/**
 * Vitrine (handoff §4). Selo único "MAIS VENDIDO" (o handoff também cita
 * "MELHOR ESCOLHA" como alternativa, mas o app só tem um selo real).
 *
 * O handoff pedia fluxo direto, sem confirmação. Mas gerar o Pix de um pacote
 * **cancela no banco** o Pix ainda aberto de outro (`pix-cobranca.service`): quem
 * mandou o primeiro código para alguém pagar o perdia sem saber. A escolha passou
 * a dizer isso antes (0094).
 */
export function VitrinePacotes({ onPago }: { onPago: () => void }) {
  const { pacotes, carregando, erro } = usePacotes();
  const [idPacoteAberto, setIdPacoteAberto] = useState<number | null>(null);

  const { janela, confirmar } = useDecisao();

  if (carregando) return <Carregando rotulo="Carregando os pacotes" />;
  if (erro) return <p className="co-erro" role="alert">{erro}</p>;

  async function escolher(p: PacoteCredito) {
    const sim = await confirmar({
      titulo: `Gerar o Pix de ${moeda(p.valorPago)}?`,
      contexto: `${p.nome} · entram ${moeda(p.valorCredito)} de crédito`,
      consequencia: 'O código Pix é gerado agora. Se você tiver um Pix de outro pacote ainda aberto, ele é cancelado no banco e deixa de valer.',
      rotuloConfirmar: 'Gerar Pix',
    });
    if (sim) setIdPacoteAberto(p.id);
  }

  return (
    <div>
      {janela}
      <div className="co-pacotes">
        {pacotes.map(p => {
          const km = kmDoPacote(p);
          return (
            <button
              key={p.id}
              type="button"
              className={`co-pacote${p.destaque ? ' co-pacote-destaque' : ''}`}
              onClick={() => escolher(p)}
            >
              <span className="co-pacote-cabeca">
                <span className="co-rotulo">{p.nome}</span>
                {p.destaque && <Selo papel="neutro">Mais vendido</Selo>}
              </span>

              <span className="co-pacote-valor">{moeda(p.valorCredito)}</span>
              <span className="co-apoio">≈ {km} km · {moeda(p.valorPorKm)} por km</span>

              <span className="co-pacote-rodape">
                <span>
                  <span className="co-rotulo">Você paga</span>
                  <span className="co-pacote-numero">{moeda(p.valorPago)}</span>
                </span>
                <span>
                  <span className="co-rotulo">Entra de crédito</span>
                  <span className="co-pacote-numero co-pacote-numero-credito">{moeda(p.valorCredito)}</span>
                  {p.valorBonus > 0 && <Selo papel="sucesso">+{moeda(p.valorBonus)}</Selo>}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <p className="co-apoio">O crédito não expira · pagamento só por Pix</p>

      <PixRecargaSheet
        idPacote={idPacoteAberto}
        onFechar={() => setIdPacoteAberto(null)}
        // Não fecha no desfecho: o cooperado precisa ler "pagamento confirmado"
        // (ou o recebido a menor). O saldo recarrega por trás.
        onPago={onPago}
      />
    </div>
  );
}
